import { useMemo, useState } from "react";
import { Copy, Download, RefreshCw, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { ExtractResult } from "@/lib/extract";
import { resultToCsv, downloadCsv } from "@/lib/csv";

interface ResultViewProps {
  file: File;
  result: ExtractResult;
  onReset: () => void;
}

export function ResultView({ file, result, onReset }: ResultViewProps) {
  const previewUrl = useMemo(() => URL.createObjectURL(file), [file]);
  const [tab, setTab] = useState<"json" | "table">("json");

  const jsonStr = useMemo(() => JSON.stringify(result.result, null, 2), [result.result]);

  function copyJSON() {
    navigator.clipboard.writeText(jsonStr);
    toast.success("JSON copied to clipboard");
  }

  function downloadJSON() {
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${file.name.replace(/\.[^.]+$/, "")}-extracted.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadCSV() {
    const csv = resultToCsv(result.result);
    if (!csv) {
      toast.error("Nothing to export.");
      return;
    }
    downloadCsv(`${file.name.replace(/\.[^.]+$/, "")}-extracted.csv`, csv);
  }

  const isImage = file.type.startsWith("image/");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="space-y-0.5">
          <h2 className="text-xl font-semibold">Extracted</h2>
          <p className="text-sm text-neutral-500">
            {file.name} · {result.processing_ms}ms · {result.confidence != null ? `${Math.round(result.confidence * 100)}% confidence` : "—"}
          </p>
        </div>
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium hover:bg-neutral-50 transition"
        >
          <RefreshCw className="h-4 w-4" />
          Process another
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg overflow-hidden border border-neutral-200 bg-white aspect-[3/4] md:aspect-auto flex items-center justify-center">
          {isImage ? (
            <img src={previewUrl} alt={file.name} className="max-w-full max-h-[600px] object-contain" />
          ) : (
            <div className="text-center p-8 text-neutral-500">
              <div className="text-5xl mb-2">📄</div>
              <p className="text-sm">{file.name}</p>
              <p className="text-xs mt-1">PDF preview not yet implemented</p>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden flex flex-col">
          <div className="flex items-center justify-between border-b border-neutral-200 px-3 py-2">
            <div className="flex gap-1 text-xs">
              {(["json", "table"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={cn(
                    "rounded px-2.5 py-1 font-medium transition",
                    tab === t ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-900",
                  )}
                >
                  {t.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              <button
                onClick={copyJSON}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100"
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
              <button
                onClick={downloadJSON}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100"
              >
                <Download className="h-3.5 w-3.5" /> JSON
              </button>
              <button
                onClick={downloadCSV}
                title="Download as CSV (one row per line item where applicable)"
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" /> CSV
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto max-h-[600px]">
            {tab === "json" ? (
              <pre className="p-4 text-sm font-mono text-neutral-800 whitespace-pre-wrap break-words">{jsonStr}</pre>
            ) : (
              <TableView data={result.result} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TableView({ data }: { data: Record<string, unknown> | null }) {
  if (!data) return <p className="p-4 text-sm text-neutral-500">No data.</p>;

  return (
    <table className="w-full text-sm">
      <tbody>
        {Object.entries(data).map(([key, value]) => (
          <tr key={key} className="border-b border-neutral-100">
            <td className="px-4 py-2 font-medium text-neutral-600 align-top w-1/3">{key}</td>
            <td className="px-4 py-2 text-neutral-900 align-top">
              {Array.isArray(value)
                ? `[${value.length} items]`
                : typeof value === "object" && value !== null
                  ? JSON.stringify(value)
                  : String(value ?? "—")}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
